import React, { useRef, useCallback, useEffect } from 'react';

/**
 * Lightweight rich-text editor using contentEditable.
 * Supports bold, italic, underline, bullet lists, and numbered lists.
 *
 * Props:
 *  - value: string (HTML)
 *  - onChange: (html: string) => void
 *  - placeholder: string
 *  - autoFocus: boolean
 *  - minHeight: string (CSS value, default '120px')
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Escribe tu nota aquí...',
  autoFocus = false,
  minHeight = '120px',
}) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Sync value prop → editor (only if different, to avoid cursor jumping)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
  }, [autoFocus]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange?.(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCmd = useCallback((command, val = null) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const ToolbarButton = ({ command, icon, title, active }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault(); // keep focus in editor
        execCmd(command);
      }}
      className={`
        w-7 h-7 flex items-center justify-center rounded-md text-[13px] transition-all duration-150
        ${active
          ? 'bg-[#0B357F] text-white shadow-sm'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        }
      `}
    >
      <span className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0" }}>
        {icon}
      </span>
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white focus-within:border-[#0B357F]/40 focus-within:ring-2 focus-within:ring-[#0B357F]/10 transition-all duration-200">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50/80">
        <ToolbarButton command="bold" icon="format_bold" title="Negrita (Ctrl+B)" />
        <ToolbarButton command="italic" icon="format_italic" title="Itálica (Ctrl+I)" />
        <ToolbarButton command="underline" icon="format_underlined" title="Subrayado (Ctrl+U)" />
        <div className="w-px h-4 bg-slate-200 mx-1" />
        <ToolbarButton command="insertUnorderedList" icon="format_list_bulleted" title="Lista con viñetas" />
        <ToolbarButton command="insertOrderedList" icon="format_list_numbered" title="Lista numerada" />
      </div>

      {/* Editor area */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          data-placeholder={placeholder}
          className="rte-editor px-3 py-2.5 text-sm text-slate-800 outline-none overflow-y-auto"
          style={{ minHeight }}
        />
      </div>

      {/* Scoped styles for the editor */}
      <style>{`
        .rte-editor:empty::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          font-style: italic;
        }
        .rte-editor ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.25em 0;
        }
        .rte-editor ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.25em 0;
        }
        .rte-editor p {
          margin: 0.15em 0;
        }
        .rte-editor b, .rte-editor strong {
          font-weight: 700;
        }
        .rte-editor i, .rte-editor em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
}
